import React, { useState } from 'react';
import ImageModal from '../components/ImageModal';
import { variantURL, fallbackToOriginal } from '../utils/imageVariant';
import LocationNotVerifiedBadge from '../components/LocationNotVerifiedBadge';
import AttendanceBadge from '../components/AttendanceBadge';
import { isCoHost } from './utils/eventHosts';

const RsvpList = ({ rsvpUsers, event, onRemove, alwaysExpanded = false }) => {
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedUserForModal, setSelectedUserForModal] = useState(null);
  const [showRsvpList, setShowRsvpList] = useState(false);

  const isExpanded = alwaysExpanded || showRsvpList;

  const listContent = (
    <div className={alwaysExpanded ? "" : "mt-4"}>
      <div className="form-group ">
        <div className="flex flex-wrap gap-y-4 justify-center">
          {rsvpUsers.length === 0 ? (
            <div className="p-4 text-gray-500 dark:text-gray-400 text-center w-full">No RSVPs yet</div>
          ) : (
            rsvpUsers.map(user => (
              <div key={user.userID} className="flex flex-col items-center text-center w-20 ">
                <div
                  className="relative cursor-pointer"
                  onClick={() => {
                    setSelectedUserForModal(user);
                    setOpenImageModal(true);
                  }}
                >
                  {user.photoURL ? (
                    <img
                      src={variantURL(user.photoURL, 200)}
                      onError={fallbackToOriginal(user.photoURL)}
                      alt={user.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <span className="text-gray-500 dark:text-gray-400 text-2xl">
                        {user.name && user.name.length > 0 ? user.name.charAt(0).toUpperCase() : '?'}
                      </span>
                    </div>
                  )}
                  {user.status === "checked-in" && (
                    <div className="absolute bottom-0 right-0 bg-green-500 rounded-full p-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  {user.approved && (
                    <div className="absolute bottom-0 left-0 bg-primary rounded-full p-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h12.5A2.25 2.25 0 0118.5 6v1.25a2.25 2.25 0 100 4.5V13a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 13v-1.25a2.25 2.25 0 100-4.5V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  {user.invited && (
                    <div className="absolute top-0 right-0 bg-primary rounded-full p-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="font-light text-gray-900 dark:text-gray-100 mt-2 text-xs overflow-hidden line-clamp-2">
                  {user.name}
                </div>
                {/* GIS-222: co-hosts are picked from this list by opening a guest's photo. */}
                {isCoHost(event, user.userID) && (
                  <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Co-host
                  </div>
                )}
                {user.status === "checked-in" && (
                  <LocationNotVerifiedBadge entry={user} className="mt-1 text-center" />
                )}
                {/* What they texted back to "Can you make it?" the day before. */}
                <AttendanceBadge entry={user.attendance} className="mt-1 text-center" />
              </div>
            ))
          )}
        </div>
        {openImageModal && selectedUserForModal && (
          <ImageModal
            selectedUser={selectedUserForModal}
            setOpen={setOpenImageModal}
            eventData={event}
            onRemove={onRemove}
            showCoHostControls
          />
        )}
      </div>
    </div>
  );

  if (alwaysExpanded) {
    return listContent;
  }

  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-stone-300 dark:border-stone-600 rounded-md">
      <button
        type="button"
        onClick={() => setShowRsvpList(!showRsvpList)}
        className="w-full flex items-center justify-between text-left focus:outline-none"
      >
        <label className="text-md font-medium text-gray-500 dark:text-gray-400">Guests ({rsvpUsers.length})</label>
        <svg
          className={`w-5 h-5 transform transition-transform ${showRsvpList ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && listContent}
    </div>
  );
};

export default RsvpList;
